// mcc/saga.js
import { put, all, takeLatest, takeEvery, select, call } from "redux-saga/effects";
import * as actionTypes from "./actionTypes";
import API from "../../commonUtils/API.js";
import { toast } from "react-toastify";
import { handleApiError } from "../../commonUtils/Functions";

// ─── helpers ──────────────────────────────────────────────────────────────────

function* getContext() {
    const extractionData = yield select((state) => state.Extractions.extractionData);
    const versionId      = yield select((state) => state.ExtVersions?.extVersion?.id);
    return { extractionData, versionId };
}

function* refreshList(extractionData, versionId) {
    const filters = yield select((state) => state.MultiChannelCampaigns.filters);
    yield put({
        type: actionTypes.GET_MULTICHANNEL_CAMPAIGNS,
        params: { extId: extractionData.id, versionId, ...filters },
    });
}

// ─── GET ALL ──────────────────────────────────────────────────────────────────

function* getMultiChannelCampaignsSaga({ params }) {
    const { versionId, ...rest } = params;
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        let queryString = "";
        if (rest.keyword) queryString += `&keyword=${rest.keyword}`;
        if (rest.status)  queryString += `&status=${rest.status}`;

        const { data } = yield API.get(
            `/ext/${params.extId}/versions/${versionId}/multichannel-campaigns?${queryString}`
        );

        if (data.success) {
            yield put({ type: actionTypes.GET_MULTICHANNEL_CAMPAIGNS_SUCCESS, payload: data.data });
        } else {
            toast.error("Oops! Something went unexpectedly wrong.");
            yield put({ type: actionTypes.GET_MULTICHANNEL_CAMPAIGNS_FAILURE, payload: data });
        }
    } catch (error) {
        handleApiError(error, "Oops! Something went unexpectedly wrong.");
        yield put({
            type: actionTypes.GET_MULTICHANNEL_CAMPAIGNS_FAILURE,
            payload: error?.response?.data || { message: "Internal server error" },
        });
    }
}

// ─── GET SINGLE ───────────────────────────────────────────────────────────────

function* getMultiChannelCampaignSaga({ id }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { extractionData, versionId } = yield call(getContext);
        if (!extractionData?.id || !versionId) {
            toast.error("Context IDs not found. Please refresh the page.");
            yield put({ type: actionTypes.GET_MULTICHANNEL_CAMPAIGN_FAILURE, payload: { message: "Context IDs not found" } });
            return;
        }

        const { data } = yield API.get(
            `/ext/${extractionData.id}/versions/${versionId}/multichannel-campaigns/${id}`
        );

        if (data.success) {
            yield put({ type: actionTypes.GET_MULTICHANNEL_CAMPAIGN_SUCCESS, payload: data.data });
        } else {
            toast.error("Could not load multichannel campaign.");
            yield put({ type: actionTypes.GET_MULTICHANNEL_CAMPAIGN_FAILURE, payload: data });
        }
    } catch (error) {
        handleApiError(error, "Could not load multichannel campaign.");
        yield put({
            type: actionTypes.GET_MULTICHANNEL_CAMPAIGN_FAILURE,
            payload: error?.response?.data || { message: "Internal server error" },
        });
    }
}

// ─── CREATE ───────────────────────────────────────────────────────────────────

function* createMultiChannelCampaignSaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { extractionData, versionId } = yield call(getContext);
        if (!extractionData?.id || !versionId) {
            toast.error("Context IDs not found. Please refresh the page.");
            yield put({ type: actionTypes.CREATE_MULTICHANNEL_CAMPAIGN_FAILURE, payload: { message: "Context IDs not found" } });
            return;
        }

        const { campaign_name, uploadFile, vendors, is_scheduled, scheduled_date, scheduled_time, vendorsComplete } = payload;

        const formData = new FormData();
        formData.append("file", uploadFile);
        formData.append("campaign_name", campaign_name);

        const { data } = yield API.post(
            `/ext/${extractionData.id}/versions/${versionId}/multichannel-campaigns`,
            formData,
            { headers: { "Content-Type": "multipart/form-data" } }
        );

        if (data.success) {
            const newCampaign = data.data;

            yield put({ type: actionTypes.CREATE_MULTICHANNEL_CAMPAIGN_SUCCESS, payload: newCampaign });
            yield call(refreshList, extractionData, versionId);

            if (vendorsComplete && vendors?.length > 0) {
                const updateBody = { vendors, is_scheduled };
                if (is_scheduled && scheduled_date && scheduled_time) {
                    updateBody.schedule_config = { scheduled_date, scheduled_time };
                }
                try {
                    const { data: updateData } = yield API.put(
                        `/ext/${extractionData.id}/versions/${versionId}/multichannel-campaigns/${newCampaign._id}`,
                        updateBody
                    );
                    if (updateData.success) {
                        yield put({ type: actionTypes.UPDATE_MULTICHANNEL_CAMPAIGN_SUCCESS, payload: updateData.data });
                    }
                } catch (e) {
                    console.error("Failed to update vendors after creation", e);
                }
                toast.success("Campaign created! Now click the Trigger button to process.");
                yield put({ type: actionTypes.SET_LOADING, payload: false });
            } else {
                toast.success("Campaign created! Now select your vendors and trigger.");
                yield put({ type: actionTypes.SET_LOADING, payload: false });
            }
        } else {
            toast.error(data?.message || "Could not create campaign.");
            yield put({ type: actionTypes.CREATE_MULTICHANNEL_CAMPAIGN_FAILURE, payload: data });
        }
    } catch (error) {
        const errorData = error?.response?.data;
        handleApiError(error, "Could not create campaign.");
        yield put({
            type: actionTypes.CREATE_MULTICHANNEL_CAMPAIGN_FAILURE,
            payload: {
                message: errorData?.message || "Internal server error",
                errors:  errorData?.data    || null,
            },
        });
    }
}

// ─── UPDATE ───────────────────────────────────────────────────────────────────

function* updateMultiChannelCampaignSaga({ payload }) {
    try {
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { extractionData, versionId } = yield call(getContext);
        if (!extractionData?.id || !versionId) {
            toast.error("Context IDs not found. Please refresh the page.");
            yield put({ type: actionTypes.UPDATE_MULTICHANNEL_CAMPAIGN_FAILURE, payload: { message: "Context IDs not found" } });
            return;
        }

        const { id, vendors, is_scheduled, scheduled_date, scheduled_time } = payload;

        const updateBody = { vendors, is_scheduled };
        if (is_scheduled && scheduled_date && scheduled_time) {
            updateBody.schedule_config = { scheduled_date, scheduled_time };
        }

        const { data } = yield API.put(
            `/ext/${extractionData.id}/versions/${versionId}/multichannel-campaigns/${id}`,
            updateBody
        );

        if (data.success) {
            yield put({ type: actionTypes.UPDATE_MULTICHANNEL_CAMPAIGN_SUCCESS, payload: data.data });
            yield call(refreshList, extractionData, versionId);

            yield call(triggerCampaignProcessSaga, {
                payload: { campaignId: id },
                extractionData,
                versionId,
            });
        } else {
            toast.error(data?.message || "Could not update multichannel campaign.");
            yield put({ type: actionTypes.UPDATE_MULTICHANNEL_CAMPAIGN_FAILURE, payload: data });
        }
    } catch (error) {
        const errorData = error?.response?.data;
        handleApiError(error, "Could not update multichannel campaign.");
        yield put({
            type: actionTypes.UPDATE_MULTICHANNEL_CAMPAIGN_FAILURE,
            payload: {
                message: errorData?.message || "Internal server error",
                errors:  errorData?.data    || null,
            },
        });
    }
}

// ─── TRIGGER ──────────────────────────────────────────────────────────────────

function* triggerCampaignProcessSaga({ payload, extractionData: ctxExtraction, versionId: ctxVersionId }) {
    try {
        let extractionData = ctxExtraction;
        let versionId      = ctxVersionId;

        if (!extractionData || !versionId) {
            const ctx = yield call(getContext);
            extractionData = ctx.extractionData;
            versionId      = ctx.versionId;
        }

        if (!extractionData?.id || !versionId) {
            toast.error("Context IDs not found. Please refresh the page.");
            yield put({ type: actionTypes.TRIGGER_CAMPAIGN_PROCESS_FAILURE, payload: { message: "Context IDs not found" } });
            return;
        }

        const { campaignId } = payload;

        const { data } = yield API.post(
            `/ext/${extractionData.id}/versions/${versionId}/multichannel-campaigns/${campaignId}/trigger`
        );

        if (data.success) {
            const campaign = data.data?.campaign;
            yield put({ type: actionTypes.TRIGGER_CAMPAIGN_PROCESS_SUCCESS, payload: campaign });
            yield call(refreshList, extractionData, versionId);

            const campaignId = campaign?._id || campaign?.id;
            if (campaignId) {
                yield put({
                    type: actionTypes.START_CAMPAIGN_POLLING,
                    payload: { campaignId },
                });
            }

            toast.success("Campaign triggered successfully!");
        } else {
            toast.error(data?.message || "Could not trigger campaign.");
            yield put({ type: actionTypes.TRIGGER_CAMPAIGN_PROCESS_FAILURE, payload: data });
        }
    } catch (error) {
        const errorData = error?.response?.data;
        handleApiError(error, "Could not trigger campaign.");
        yield put({
            type: actionTypes.TRIGGER_CAMPAIGN_PROCESS_FAILURE,
            payload: {
                message: errorData?.message || "Internal server error",
                errors:  errorData?.data    || null,
            },
        });
    }
}

// ─── DELETE ───────────────────────────────────────────────────────────────────

function* deleteMultiChannelCampaignSaga({ Id }) {
    try {
        const { extractionData, versionId } = yield call(getContext);
        yield put({ type: actionTypes.SET_LOADING, payload: true });

        const { data } = yield API.delete(
            `/ext/${extractionData.id}/versions/${versionId}/multichannel-campaigns/${Id}`
        );

        if (data.success) {
            yield put({ type: actionTypes.DELETE_MULTICHANNEL_CAMPAIGN_SUCCESS, payload: data });
            yield call(refreshList, extractionData, versionId);
        } else {
            toast.error(data?.message || "Could not delete multichannel campaign.");
            yield put({ type: actionTypes.DELETE_MULTICHANNEL_CAMPAIGN_FAILURE, payload: data });
        }
    } catch (error) {
        handleApiError(error, "Could not delete multichannel campaign.");
        const data = error?.response?.data;
        yield put({
            type: actionTypes.DELETE_MULTICHANNEL_CAMPAIGN_FAILURE,
            payload: data || { message: "Internal server error" },
        });
    }
}

// ─── FETCH ACEFONE LEAD LISTS ─────────────────────────────────────────────────

function* fetchAcefoneLeadListsSaga({ versionId }) {
    try {
        const { data } = yield API.get(`/mcc-services/acefone-lead-lists/${versionId}`);

        if (data.success) {
            yield put({ type: actionTypes.FETCH_ACEFONE_LEAD_LISTS_SUCCESS, payload: data.data });
        } else {
            toast.error("Could not load Acefone lead lists.");
            yield put({ type: actionTypes.FETCH_ACEFONE_LEAD_LISTS_FAILURE, payload: data });
        }
    } catch (error) {
        handleApiError(error, "Could not load Acefone lead lists.");
        yield put({
            type: actionTypes.FETCH_ACEFONE_LEAD_LISTS_FAILURE,
            payload: error?.response?.data || { message: "Internal server error" },
        });
    }
}

// ─── RETRY CAMPAIGN OUTPUT (polling tick) ─────────────────────────────────────
/**
 * Uses takeEvery (not takeLatest) so concurrent polls for different campaigns
 * don't cancel each other.
 * On terminal status, also refreshes the full campaign list.
 */
function* retryCampaignOutputSaga({ payload }) {
    try {
        const { extractionData, versionId } = yield call(getContext);
        if (!extractionData?.id || !versionId) return;

        const { campaignId } = payload;

        const { data } = yield API.post(
            `/ext/${extractionData.id}/versions/${versionId}/multichannel-campaigns/${campaignId}/retry-output`
        );

        if (data.success) {
            yield put({
                type: actionTypes.RETRY_CAMPAIGN_OUTPUT_SUCCESS,
                payload: data.data, // { campaign, status }
            });

            // If terminal, do a full list refresh to ensure consistency
            const terminalStatuses = ['profiles_extracted', 'service_triggered', 'completed', 'failed', 'cancelled'];
            if (terminalStatuses.includes(data.data.status)) {
                yield call(refreshList, extractionData, versionId);
            }
        } else {
            yield put({ type: actionTypes.RETRY_CAMPAIGN_OUTPUT_FAILURE, payload: data });
        }
    } catch (error) {
        // Silent failure — polling should not surface transient errors to the user
        yield put({
            type: actionTypes.RETRY_CAMPAIGN_OUTPUT_FAILURE,
            payload: error?.response?.data || { message: "Polling error" },
        });
    }
}

function* triggerMCCServiceSaga({ payload }) {
    try {
        const { extractionData, versionId } = yield call(getContext);
        if (!extractionData?.id || !versionId) return;

        const { campaignId } = payload;
        const { data } = yield API.post(
            `/ext/${extractionData.id}/versions/${versionId}/multichannel-campaigns/${campaignId}/trigger-service`
        )
        // console.log("data", data);

        if (data.success) {
            toast.success("Multichannel service triggered successfully!");
            yield call(refreshList, extractionData, versionId);
        } else {
            toast.error(data?.message || "Could not trigger Multichannel service.");
        }
    } catch (error) {
        handleApiError(error, "Could not trigger Multichannel service.");
    }
}

// ─── RETRY CAMPAIGN OUTPUT (polling tick) ─────────────────────────────────────
/**
 * Uses takeEvery (not takeLatest) so concurrent polls for different campaigns
 * don't cancel each other.
 * On terminal status, also refreshes the full campaign list.
 */
// function* retryCampaignOutputSaga({ payload }) {
//     try {
//         const { extractionData, versionId } = yield call(getContext);
//         if (!extractionData?.id || !versionId) return;
// ─── Root saga ────────────────────────────────────────────────────────────────

function* MultiChannelCampaignsSaga() {
    yield all([
        takeLatest(actionTypes.GET_MULTICHANNEL_CAMPAIGNS,   getMultiChannelCampaignsSaga),
        takeLatest(actionTypes.GET_MULTICHANNEL_CAMPAIGN,    getMultiChannelCampaignSaga),
        takeLatest(actionTypes.CREATE_MULTICHANNEL_CAMPAIGN, createMultiChannelCampaignSaga),
        takeLatest(actionTypes.UPDATE_MULTICHANNEL_CAMPAIGN, updateMultiChannelCampaignSaga),
        takeLatest(actionTypes.DELETE_MULTICHANNEL_CAMPAIGN, deleteMultiChannelCampaignSaga),
        takeLatest(actionTypes.TRIGGER_CAMPAIGN_PROCESS,     triggerCampaignProcessSaga),
        takeLatest(actionTypes.FETCH_ACEFONE_LEAD_LISTS,     fetchAcefoneLeadListsSaga),
        takeEvery(actionTypes.RETRY_CAMPAIGN_OUTPUT,         retryCampaignOutputSaga), // takeEvery — concurrent campaigns
        takeLatest(actionTypes.TRIGGER_MCC_SERVICE,          triggerMCCServiceSaga)
    ]);
}

export default MultiChannelCampaignsSaga;